import { useEffect, useCallback } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Eraser, ShieldCheck } from '@phosphor-icons/react';
import SignatureCanvas from 'react-signature-canvas';

const SIGNATURE_STATUSES = [
  { value: 'SIGNED', label: 'Patient Signed' },
  { value: 'REFUSE_TO_SIGN', label: 'Patient Refused to Sign' },
  { value: 'UNABLE_TO_SIGN', label: 'Patient Unable to Sign' },
  { value: 'MINOR', label: 'Minor (Parent/Guardian Signs)' },
];

const AUTHORIZED_DISCLAIMER =
  "By signing below, I certify that the patient is unable to sign this form at this time. I am authorized to sign on behalf of the patient and acknowledge that the information provided on this form is accurate to the best of my knowledge.";

export function SignatureSection({
  type,
  title,
  iconColor,
  legalText,
  minorDisclaimer,
  formData,
  handleInputChange,
  signatureRef,
  guardianSignatureRef,
  clearSignature,
  clearGuardianSignature
}) {
  const statusKey = `${type}_signature_status`;
  const dateKey = `${type}_date`;
  const signatureKey = `${type}_signature`;
  const guardianNameKey = `${type}_guardian_name`;
  const guardianSignatureKey = `${type}_guardian_signature`;
  const authorizedNameKey = `${type}_authorized_name`;
  const authorizedRelationKey = `${type}_authorized_relation`;
  const status = formData[statusKey] || '';
  const existingSignature = formData[signatureKey];
  const existingGuardianSignature = formData[guardianSignatureKey];

  // Persist signature to formData when user finishes drawing
  const handleSignatureEnd = useCallback(() => {
    if (signatureRef?.current && !signatureRef.current.isEmpty()) {
      const dataUrl = signatureRef.current.toDataURL();
      handleInputChange(signatureKey, dataUrl);
    }
  }, [signatureRef, signatureKey, handleInputChange]);

  const handleGuardianSignatureEnd = useCallback(() => {
    if (guardianSignatureRef?.current && !guardianSignatureRef.current.isEmpty()) {
      const dataUrl = guardianSignatureRef.current.toDataURL();
      handleInputChange(guardianSignatureKey, dataUrl);
    }
  }, [guardianSignatureRef, guardianSignatureKey, handleInputChange]);

  // Restore signature from formData when canvas mounts
  useEffect(() => {
    if (status === 'SIGNED' && existingSignature && signatureRef?.current) {
      const timer = setTimeout(() => {
        try {
          if (signatureRef.current && signatureRef.current.isEmpty()) {
            signatureRef.current.fromDataURL(existingSignature, { ratio: 1 });
          }
        } catch { /* canvas may not be ready */ }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [status, existingSignature, signatureRef]);

  // Restore guardian signature
  useEffect(() => {
    if ((status === 'MINOR' || status === 'UNABLE_TO_SIGN') && existingGuardianSignature && guardianSignatureRef?.current) {
      const timer = setTimeout(() => {
        try {
          if (guardianSignatureRef.current && guardianSignatureRef.current.isEmpty()) {
            guardianSignatureRef.current.fromDataURL(existingGuardianSignature, { ratio: 1 });
          }
        } catch { /* canvas may not be ready */ }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [status, existingGuardianSignature, guardianSignatureRef]);

  const handleClearMain = () => {
    clearSignature?.();
    handleInputChange(signatureKey, '');
  };

  const handleClearGuardian = () => {
    clearGuardianSignature?.();
    handleInputChange(guardianSignatureKey, '');
  };

  const showPatientSigCanvas = status === 'SIGNED';
  const showGuardianSection = status === 'MINOR';
  const showAuthorizedSection = status === 'UNABLE_TO_SIGN';

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-4">
        <CardTitle className="text-xl flex items-center gap-2">
          <ShieldCheck size={24} className={iconColor} />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-slate-50 p-4 rounded-sm text-sm text-slate-700 whitespace-pre-wrap max-h-48 overflow-y-auto">
          {legalText}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Signature Status
            </Label>
            <Select
              value={status}
              onValueChange={(value) => handleInputChange(statusKey, value)}
            >
              <SelectTrigger className="rounded-sm border-slate-300" data-testid={`${type}-signature-status-select`}>
                <SelectValue placeholder="Select signature status" />
              </SelectTrigger>
              <SelectContent>
                {SIGNATURE_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Date
            </Label>
            <Input
              type="date"
              value={formData[dateKey] || ''}
              onChange={(e) => handleInputChange(dateKey, e.target.value)}
              className="rounded-sm border-slate-300"
              data-testid={`${type}-date-input`}
            />
          </div>
        </div>

        {/* Patient Signature Canvas */}
        {showPatientSigCanvas && (
          <div className="space-y-2 animate-in fade-in duration-200">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Patient Signature
            </Label>
            {existingSignature && (
              <div className="mb-2">
                <p className="text-xs text-green-600 font-medium">Signature captured</p>
              </div>
            )}
            <div className="border border-slate-300 rounded-sm bg-white">
              <SignatureCanvas
                ref={signatureRef}
                penColor="black"
                onEnd={handleSignatureEnd}
                canvasProps={{
                  className: 'w-full h-32',
                  'data-testid': `${type}-signature-canvas`
                }}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClearMain}
              className="rounded-sm"
              data-testid={`${type}-clear-signature-button`}
            >
              <Eraser size={16} className="mr-2" />
              Clear Signature
            </Button>
          </div>
        )}

        {/* Authorized Representative Signature (Unable to Sign) */}
        {showAuthorizedSection && (
          <div className="space-y-4 animate-in fade-in duration-200 border-t border-slate-200 pt-4">
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Authorized Representative Signature</h4>
              <p className="text-sm text-blue-800">{AUTHORIZED_DISCLAIMER}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Authorized Representative Name
                </Label>
                <Input
                  value={formData[authorizedNameKey] || ''}
                  onChange={(e) => handleInputChange(authorizedNameKey, e.target.value)}
                  placeholder="Full name of authorized representative"
                  className="rounded-sm border-slate-300"
                  data-testid={`${type}-authorized-name-input`}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Relationship to Patient
                </Label>
                <Input
                  value={formData[authorizedRelationKey] || ''}
                  onChange={(e) => handleInputChange(authorizedRelationKey, e.target.value)}
                  placeholder="e.g., Spouse, Parent, Power of Attorney"
                  className="rounded-sm border-slate-300"
                  data-testid={`${type}-authorized-relation-input`}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Authorized Representative Signature
              </Label>
              {existingGuardianSignature && (
                <div className="mb-2">
                  <p className="text-xs text-green-600 font-medium">Signature captured</p>
                </div>
              )}
              <div className="border border-slate-300 rounded-sm bg-white">
                <SignatureCanvas
                  ref={guardianSignatureRef}
                  penColor="black"
                  onEnd={handleGuardianSignatureEnd}
                  canvasProps={{
                    className: 'w-full h-32',
                    'data-testid': `${type}-guardian-signature-canvas`
                  }}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleClearGuardian}
                className="rounded-sm"
                data-testid={`${type}-guardian-clear-signature-button`}
              >
                <Eraser size={16} className="mr-2" />
                Clear Signature
              </Button>
            </div>
          </div>
        )}

        {/* Minor - Parent/Guardian Signature */}
        {showGuardianSection && (
          <div className="space-y-4 animate-in fade-in duration-200 border-t border-slate-200 pt-4">
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
              <h4 className="font-semibold text-amber-900 mb-2">Parent / Legal Guardian Signature</h4>
              <p className="text-sm text-amber-800">{minorDisclaimer}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Parent / Legal Guardian Name
              </Label>
              <Input
                value={formData[guardianNameKey] || ''}
                onChange={(e) => handleInputChange(guardianNameKey, e.target.value)}
                placeholder="Full name of parent or legal guardian"
                className="rounded-sm border-slate-300"
                data-testid={`${type}-guardian-name-input`}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Parent / Legal Guardian Signature
              </Label>
              {existingGuardianSignature && (
                <div className="mb-2">
                  <p className="text-xs text-green-600 font-medium">Signature captured</p>
                </div>
              )}
              <div className="border border-slate-300 rounded-sm bg-white">
                <SignatureCanvas
                  ref={guardianSignatureRef}
                  penColor="black"
                  onEnd={handleGuardianSignatureEnd}
                  canvasProps={{
                    className: 'w-full h-32',
                    'data-testid': `${type}-guardian-signature-canvas`
                  }}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleClearGuardian}
                className="rounded-sm"
                data-testid={`${type}-guardian-clear-signature-button`}
              >
                <Eraser size={16} className="mr-2" />
                Clear Signature
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
